"use strict";const _jsxFileName = "/home/runner/work/bloom-mono/bloom-mono/apps/frontend/webChat/src/patientResponse/PatientPage.tsx";Object.defineProperty(exports, "__esModule", {value: true}); function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; } function _nullishCoalesce(lhs, rhsFn) { if (lhs != null) { return lhs; } else { return rhsFn(); } } function _optionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }var _react = require('react'); var _react2 = _interopRequireDefault(_react);

var _urls = require('web/services/urls');
var _classBuilder = require('shared/services/classBuilder'); var _classBuilder2 = _interopRequireDefault(_classBuilder);
var _TextKindLabel = require('web/components/SecureText/TextKindLabel');
var _brandPalette = require('shared/services/brandPalette');

var _AppPrompts = require('./AppPrompts');
var _ClinicHelp = require('./ClinicHelp'); var _ClinicHelp2 = _interopRequireDefault(_ClinicHelp);

/* The secure patient page's own shell (round 4): the purple surround and a
 * rounded white card, inset on phones and up to 900 px wide on desktop.
 * Help is the ? at the right of the caption row (D-12), with the lock just
 * before it to mark the chat as secure (a label, not a control). With a
 * banner above (the app bar, or Apple's in iOS Safari) a "Secured by
 * BloomText" line would repeat the brand, so it is dropped; otherwise it
 * sits centred on purple above the card: "Secured by" and the white logo. It replaces the shared anonymous-page wrapper here only, so the
 * login, confirm-email and video-chat pages keep their header and footer.
 *
 * Clinic branding (P-2): with a clinic that may brand, the caption becomes
 * its logo alone on a white plate (or stays "From: <clinic>" without one, or
 * when the logo fails to load), the page takes the clinic's derived colours
 * as CSS variables, and the ? opens the clinic's contact card when it has
 * one. The purple surround and "Secured by BloomText" stay. */

// Read once at load: the desktop app opens the homepage in a new window
// rather than navigating the app away.
const HOMEPAGE_TARGET = window.ELECTRON ? '_blank' : '_self'

const HelpIcon = () => (
  _react2.default.createElement('svg', { viewBox: "0 0 24 24"   , width: "22", height: "22", fill: "none", stroke: "currentColor", strokeWidth: "1.7", strokeLinecap: "round", strokeLinejoin: "round", 'aria-hidden': "true", __self: this, __source: {fileName: _jsxFileName, lineNumber: 31}}
    , _react2.default.createElement('circle', { cx: "12", cy: "12", r: "9", __self: this, __source: {fileName: _jsxFileName, lineNumber: 32}} )
    , _react2.default.createElement('path', { d: "M9 9a3 3 0 0 1 6 0c0 2-3 2-3 4M12 17h.01"           , __self: this, __source: {fileName: _jsxFileName, lineNumber: 33}} )
  )
)

/* How the page meets the on-screen keyboard, the same in every phone
 * browser (D-18, D-19): the whole page slides up with it, as Safari does for
 * any page. The browser keeps the layout viewport, pans the visual one up by
 * the keyboard's height and scrolls the message box into view, so the header
 * may leave the top while the patient types. The card keeps its round
 * corners and the purple strip under it, which holds the composer clear of
 * the browser's own bars, and the purple behind the page fills whatever
 * shows around it (#1179). Desktop has no on-screen keyboard; there the page
 * isn't fixed and nothing here changes it. */
const KEYBOARD_MIN_HEIGHT = 150
// Long enough for the browser's own pan to finish first.
const REVEAL_AFTER_MS = 300
// Long enough for a keyboard coming up to have shrunk the visual viewport.
const SETTLE_AFTER_MS = 250
// Chrome and Firefox on Android shrink the layout viewport for the keyboard,
// which compresses a fixed page instead of sliding it. While the page is
// open, the viewport asks them to resize only the visual viewport, as Safari
// does; browsers that don't know the setting ignore it.
const INTERACTIVE_WIDGET = 'interactive-widget=resizes-visual'

const useKeyboardSlide = () => {
  const ref = _react.useRef(null)

  _react.useLayoutEffect.call(void 0, () => {
    const meta = document.querySelector('meta[name="viewport"]')
    const content = _optionalChain([meta, 'optionalAccess', _ => _.getAttribute, 'call', _2 => _2('content')])
    if (!meta || content == null || content.includes('interactive-widget')) return undefined
    meta.setAttribute('content', `${content}, ${INTERACTIVE_WIDGET}`)
    return () => meta.setAttribute('content', content)
  }, [])

  _react.useLayoutEffect.call(void 0, () => {
    const viewport = window.visualViewport
    const page = ref.current
    if (!viewport || !page) return undefined

    // A pinch zoom also shrinks the visual viewport; it isn't the keyboard.
    const isZoomed = () => Math.abs(viewport.scale - 1) > 0.01

    const markKeyboard = () => {
      if (isZoomed()) return
      // The layout viewport keeps its height under the keyboard; no browser
      // bar is this tall, only a keyboard.
      page.classList.toggle(
        'patient-page--keyboard-open',
        document.documentElement.clientHeight - viewport.height > KEYBOARD_MIN_HEIGHT,
      )
    }

    // A browser sometimes skips its own pan (Safari, right after its address
    // bar had focus), leaving the message box under the keyboard. Once the
    // keyboard is up, a composer still below the visible area is scrolled up
    // to sit on it; after the browser's own pan this does nothing.
    let revealTimer
    const reveal = () => {
      const field = document.activeElement
      if (!(field instanceof HTMLElement) || !page.contains(field)) return
      const composer = _nullishCoalesce(field.closest('.patient-conversation--input-wrapper'), () => ( field))
      const hidden = composer.getBoundingClientRect().bottom - (viewport.offsetTop + viewport.height)
      if (hidden > 1) window.scrollBy(0, hidden)
    }
    // With the keyboard gone, however it went (Done, a tap elsewhere, a drag,
    // or a hardware keyboard taking over while the field keeps focus), the
    // browser can leave the page scrolled up by the keyboard's height. The
    // page is fixed and fills the viewport, so any scroll left then is that
    // pan: put it back. Checked after a pause, so it never undoes the slide
    // while the keyboard is still coming up.
    let settleTimer
    const settle = () => {
      if (isZoomed() || page.classList.contains('patient-page--keyboard-open')) return
      if (window.getComputedStyle(page).position !== 'fixed') return
      if (window.scrollY !== 0 || viewport.offsetTop !== 0) window.scrollTo(0, 0)
    }
    const settleSoon = () => {
      clearTimeout(settleTimer)
      settleTimer = setTimeout(settle, SETTLE_AFTER_MS)
    }

    const onResize = () => {
      markKeyboard()
      clearTimeout(revealTimer)
      if (page.classList.contains('patient-page--keyboard-open')) revealTimer = setTimeout(reveal, REVEAL_AFTER_MS)
      else settleSoon()
    }

    markKeyboard()
    viewport.addEventListener('resize', onResize)
    viewport.addEventListener('scroll', settleSoon)
    window.addEventListener('scroll', settleSoon)
    return () => {
      clearTimeout(revealTimer)
      clearTimeout(settleTimer)
      viewport.removeEventListener('resize', onResize)
      viewport.removeEventListener('scroll', settleSoon)
      window.removeEventListener('scroll', settleSoon)
    }
  }, [])

  return ref
}

// The clinic's logo in place of the caption; the caption again if it fails.
const ClinicCaption = ({ clinic, caption }) => {
  const [failed, setFailed] = _react.useState(null)
  const logo = _optionalChain([clinic, 'optionalAccess', _3 => _3.LogoURL])
  if (!logo || failed === logo) return _react2.default.createElement('div', { className: "patient-page--caption", __self: this, __source: {fileName: _jsxFileName, lineNumber: 142}},  caption )
  return (
    _react2.default.createElement('div', { className: "patient-page--caption patient-page--caption-logo" , __self: this, __source: {fileName: _jsxFileName, lineNumber: 144}}
      , _react2.default.createElement('span', { className: "patient-page--clinic-logo", __self: this, __source: {fileName: _jsxFileName, lineNumber: 145}}
        , _react2.default.createElement('img', { src: logo, alt: _nullishCoalesce(clinic.Name, () => ( '')), onError: () => setFailed(logo), __self: this, __source: {fileName: _jsxFileName, lineNumber: 146}} )
      )
    )
  )
}










 const PatientPage = ({ caption, prompt, clinic, className, children }) => {
  const pageRef = useKeyboardSlide()
  const paletteStyle = _react.useMemo.call(void 0, () => _brandPalette.brandPaletteStyle.call(void 0, _brandPalette.brandPalette.call(void 0, _optionalChain([clinic, 'optionalAccess', _4 => _4.Color]))), [_optionalChain([clinic, 'optionalAccess', _5 => _5.Color])])

  return (
    _react2.default.createElement('div', { className: "patient-page", ref: pageRef, style: paletteStyle, __self: this, __source: {fileName: _jsxFileName, lineNumber: 166}}
      , _react2.default.createElement(_AppPrompts.GetTheAppBar, { prompt: prompt, __self: this, __source: {fileName: _jsxFileName, lineNumber: 167}} )
      , _react2.default.createElement('div', { className: "patient-page--surround", __self: this, __source: {fileName: _jsxFileName, lineNumber: 168}}
        ,  !prompt.bannerAbove && (
          _react2.default.createElement('header', { className: "patient-page--brand-row", __self: this, __source: {fileName: _jsxFileName, lineNumber: 170}}
            , _react2.default.createElement('a', {
              href: _urls.homepageUrl,
              target: HOMEPAGE_TARGET,
              className: "patient-page--secured-by",
              'aria-label': "Secured by BloomText"  , __self: this, __source: {fileName: _jsxFileName, lineNumber: 171}}
, "Secured by"

              , _react2.default.createElement('img', { src: "/assets/images/bloomtext-logo.svg", alt: "", width: "80", height: "24", __self: this, __source: {fileName: _jsxFileName, lineNumber: 178}} )
            )
          )
        )
        , _react2.default.createElement('main', { className: "patient-page--card", __self: this, __source: {fileName: _jsxFileName, lineNumber: 182}}
          , _react2.default.createElement('div', { className: "patient-page--caption-row", __self: this, __source: {fileName: _jsxFileName, lineNumber: 183}}
            , _react2.default.createElement(ClinicCaption, { key: _optionalChain([clinic, 'optionalAccess', _6 => _6.OrgKey]), clinic: clinic, caption: caption, __self: this, __source: {fileName: _jsxFileName, lineNumber: 184}} )
            , _react2.default.createElement('img', { className: "patient-page--secure-mark", src: _TextKindLabel.LOCK_ICON_SRC, alt: "Secure chat" , width: "14", height: "17", __self: this, __source: {fileName: _jsxFileName, lineNumber: 185}} )
            ,  _optionalChain([clinic, 'optionalAccess', _7 => _7.Contact])
              ? _react2.default.createElement(_ClinicHelp2.default, { clinic: clinic, __self: this, __source: {fileName: _jsxFileName, lineNumber: 187}}, _react2.default.createElement(HelpIcon, {__self: this, __source: {fileName: _jsxFileName, lineNumber: 187}} ))
              : (
                  _react2.default.createElement('a', {
                    href: _urls.supportUrl,
                    target: "_blank",
                    rel: "noopener noreferrer" ,
                    className: "patient-page--inline-help",
                    'aria-label': "Need help?" , __self: this, __source: {fileName: _jsxFileName, lineNumber: 189}}

                    , _react2.default.createElement(HelpIcon, {__self: this, __source: {fileName: _jsxFileName, lineNumber: 196}} )
                  )
                )
          )
          , _react2.default.createElement('div', { className: _classBuilder2.default.call(void 0, 'patient-page--content', className), __self: this, __source: {fileName: _jsxFileName, lineNumber: 200}}
            ,  children 
          )
          ,  prompt.showsManualLink && _react2.default.createElement(_AppPrompts.ManualAppLink, {__self: this, __source: {fileName: _jsxFileName, lineNumber: 203}} ) 
        )
      )
    )
  )
}; exports.PatientPage = PatientPage
